import { ReactNode, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import loadIcon from 'images/load-icon.gif';
import {
    getOQAdministrationURL,
    sendOQAdministrationSMS,
    sendOQAdministrationEmail,
    getOQSchedule,
    setOQSchedule,
    stopOQSchedule,
    getPatientDeviceStatus,
    sendOQAdministrationPush,
} from './actions';
import { OQScheduleChannel, OQScheduleCadence } from './types';
import { displayPhone } from './phone';
import {
    HOUR_OPTIONS,
    defaultStart,
    formatSendAt,
    fromStartAtISO,
    toStartAtISO,
} from './scheduleTime';

const IN_APP_INSTRUMENT_ID = '1';

const measuresList = [
    ['1', 'OQ®-45.2 English'],
    ['121', 'CSSRS English'],
    ['9', 'OQ®-ASC English'],
];

// Channels the scheduler can deliver on. `push` creates an in-app measure
// request and pushes for it (the gateway's services/measure-request-push.js, the
// same seam the manual "Send to iPhone app" uses), so it needs a registered
// device — see schedulePushHint below.
const channelOptions = [
    ['email', 'Email'],
    ['sms', 'SMS (text)'],
    ['push', 'iPhone app'],
];

const cadenceOptions = [
    ['weekly', 'Weekly'],
    ['biweekly', 'Every 2 weeks'],
    ['monthly', 'Monthly'],
];

const channelLabels: Record<string, string> = { email: 'Email', sms: 'SMS', push: 'iPhone app' };

// The manual-send channels, in the order the "Send to" dropdown offers them.
// Text message first because it is the clinic's habitual channel; the default
// falls back to email when the patient has no phone on file.
type SendChannel = 'sms' | 'email' | 'push';
const SEND_CHANNELS: SendChannel[] = ['sms', 'email', 'push'];
const defaultSendChannel = (hasPhone: boolean): SendChannel => (hasPhone ? 'sms' : 'email');

// Everything the single send row needs to know about one channel. Built per
// render from the same flags the three separate rows used to read, so the
// enabled/hint/spinner rules per channel are unchanged — only where they show.
type SendChannelDescriptor = {
    optionLabel: string;
    buttonLabel: string;
    inFlight: boolean;
    disabled: boolean;
    hint: string | null;
    onConfirm: () => void;
    confirmation: ReactNode;
};
const cadenceLabels: Record<string, string> = {
    weekly: 'Weekly',
    biweekly: 'Every 2 weeks',
    monthly: 'Monthly',
};

// The confirm-and-send button. Every manual send in this dialog (text, email,
// iPhone app) is the same widget: a button that opens a Confirmation modal, a
// muted hint (usually why it is disabled; for the iPhone app it can also be a
// non-blocking note under an enabled button), a spinner while the send is in
// flight, and Cancel/Confirm. The "Send to" dropdown picks which channel's
// descriptor feeds it; keying the element on the channel remounts it on a
// change, so a half-open confirmation for one channel can never be confirmed
// as another.
//
// Declared at module scope, NOT inside AdministrationModal's render body,
// because it owns its own `showConfirmation` state: a component defined in the
// render body is a brand-new type on every render, so React unmounts and
// remounts it and the open/closed state would be lost.
type ConfirmSendButtonProps = {
    label: string;
    confirmation: ReactNode;
    onConfirm: () => void;
    inFlight: boolean;
    disabled?: boolean;
    hint?: string | null;
    className?: string;
};

const ConfirmSendButton = ({
    label,
    confirmation,
    onConfirm,
    inFlight,
    disabled = false,
    hint = null,
    className,
}: ConfirmSendButtonProps): JSX.Element => {
    const [showConfirmation, setShowConfirmation] = useState(false);

    return (
        <div className={className}>
            <button
                className="position-relative btn btn-primary primary-btn-color px-3 py-2 font-10"
                onClick={() => setShowConfirmation(true)}
                disabled={disabled || inFlight}
            >
                {inFlight &&
                    <img
                        className="position-absolute m-0 p-0"
                        style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}
                        src={loadIcon}
                        alt="Loading..."
                        height="20px"
                    />}
                <span style={{ color: inFlight ? 'transparent' : 'inherit' }}>{label}</span>
            </button>
            {hint && (
                <small className="text-muted font-10 mt-1">{hint}</small>
            )}
            {showConfirmation && (
                <Modal
                    className="modal-dialog-centered custom-modal"
                    isOpen={showConfirmation}
                    toggle={() => setShowConfirmation(false)}
                >
                    <ModalHeader toggle={() => setShowConfirmation(false)}>Confirmation</ModalHeader>
                    <ModalBody>
                        <span>{confirmation}</span>
                        <div className="d-flex flex-row justify-content-between w-100 mt-4">
                            <button
                                className="btn btn-white bg-white btn-lg btn-block border mt-2 font-15 mr-2"
                                onClick={() => setShowConfirmation(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary btn-lg btn-block btn-color font-15"
                                onClick={() => {
                                    onConfirm();
                                    setShowConfirmation(false);
                                }}
                            >
                                Confirm
                            </button>
                        </div>
                    </ModalBody>
                </Modal>
            )}
        </div>
    );
};

type Props = {
    isOpen: boolean;
    toggle: () => void;
    client: ClientData;
}

const AdministrationModal = (props: Props): JSX.Element => {
    const { isOpen, toggle, client } = props;
    const {
        administrationURL,
        isFetchingURL,
        isSendingSMS,
        isSendingEmail,
        deviceStatus,
        isLoadingDeviceStatus,
        deviceStatusError,
        isSendingPush,
        schedule,
        isSavingSchedule,
    } = useSelector((state: RootState) => state.get('measures').toJS());
    const dispatch = useDispatch();

    const [tooltipMessage, setTooltipMessage] = useState('Copy to Clipboard');
    const [selectedMeasure, setSelectedMeasure] = useState('1'); // Default to OQ 45.2
    const [scheduleChannel, setScheduleChannel] = useState<OQScheduleChannel>('email');
    const [scheduleCadence, setScheduleCadence] = useState<OQScheduleCadence>('weekly');
    // First-send date + hour in the admin's local time (sends fire on the hourly tick).
    const [startDate, setStartDate] = useState<string>(() => defaultStart().dateValue);
    const [startHour, setStartHour] = useState<number>(() => defaultStart().hour);

    const patientId = client?.patientId?.toString();
    const clientPhone = client?.client?.phone;
    const hasPhone = Boolean(clientPhone);
    const phoneForDisplay = displayPhone(clientPhone);
    const clientEmail = client?.client?.email;
    // Which manual-send channel the "Send to" dropdown has selected.
    const [sendChannel, setSendChannel] = useState<SendChannel>(() => defaultSendChannel(hasPhone));
    const selectedMeasureLabel = measuresList.find(([value]) => value === selectedMeasure)?.[1];

    // Part A: generate the measure URL implicitly when the dialog opens (and
    // whenever the measure changes) — no explicit "generate" step.
    useEffect(() => {
        if (isOpen && patientId) {
            dispatch(getOQAdministrationURL({ patientId, instrumentIds: selectedMeasure }));
        }
    }, [isOpen, patientId, selectedMeasure, dispatch]);

    // The dropdown resets to the default channel every time the dialog opens
    // (it is reused across clients from PatientActionsCard, so a channel picked
    // for one patient must not carry over to the next).
    useEffect(() => {
        if (isOpen) setSendChannel(defaultSendChannel(hasPhone));
    }, [isOpen, hasPhone]);

    // Load the patient's current automated-send schedule when the dialog opens.
    useEffect(() => {
        if (isOpen && patientId) {
            dispatch(getOQSchedule({ patientId }));
        }
    }, [isOpen, patientId, dispatch]);

    // "Send to iPhone app" is only offered when the patient's app has
    // registered a push device, so look that up on open too.
    useEffect(() => {
        if (isOpen && patientId) {
            dispatch(getPatientDeviceStatus({ patientId }));
        }
    }, [isOpen, patientId, dispatch]);

    // Reflect a loaded schedule into the form controls.
    // Depend on the schedule's PRIMITIVES, not the object: `toJS()` above yields a
    // fresh `schedule` object every render, so `[schedule]` would re-run this after
    // each keystroke and silently revert the admin's edits (found in review,
    // 2026-09-20 — "Update schedule" could never change anything).
    const scheduleId = schedule?.id;
    const scheduleNextSendAt = schedule?.nextSendAt ?? null;
    const loadedChannel = schedule?.channel;
    const loadedCadence = schedule?.cadence;
    useEffect(() => {
        if (!scheduleId) return;
        if (loadedChannel) setScheduleChannel(loadedChannel);
        if (loadedCadence) setScheduleCadence(loadedCadence);
        const next = scheduleNextSendAt ? fromStartAtISO(scheduleNextSendAt) : null;
        if (next) {
            setStartDate(next.dateValue);
            setStartHour(next.hour);
        }
    }, [scheduleId, scheduleNextSendAt, loadedChannel, loadedCadence]);

    // Whether the patient's app has registered a push device. Gates both the
    // manual "Send to iPhone app" below and the schedule's push channel, so it is
    // declared ahead of the first of the two.
    const hasDevice = Boolean(deviceStatus?.hasDevice);

    const startAt = toStartAtISO(startDate, startHour);
    const startAtInPast = !!startAt && new Date(startAt).getTime() <= Date.now();
    // A push schedule for a patient with no registered device would fail on every
    // hourly tick, so the option is greyed and saving it is blocked. A schedule
    // already stored as `push` keeps showing "iPhone app" after the device goes
    // away — never silently rewritten to email — so what we block is the save,
    // and picking any other channel re-enables it.
    const schedulePushBlocked = scheduleChannel === 'push' && !hasDevice;
    const canSaveSchedule = !isSavingSchedule && !!startAt && !startAtInPast && !schedulePushBlocked;

    const handleSendSMS = () => {
        dispatch(
            sendOQAdministrationSMS({
                patientId,
                phoneNumber: client.client.phone,
                instrumentIds: selectedMeasure,
            })
        );
    };

    const handleSendEmail = () => {
        // No address in the payload: the gateway reads the patient's email on
        // file, so a manual send can never reach a different inbox than the
        // automated schedule would.
        dispatch(sendOQAdministrationEmail({ patientId, instrumentIds: selectedMeasure }));
    };

    const handleSendPush = () => {
        dispatch(sendOQAdministrationPush({ patientId, instrumentIds: selectedMeasure }));
    };

    const handleSaveSchedule = () => {
        if (!startAt) return;
        dispatch(
            setOQSchedule({
                patientId,
                channel: scheduleChannel,
                cadence: scheduleCadence,
                startAt,
                instrumentIds: selectedMeasure,
            })
        );
    };

    const handleStopSchedule = () => {
        dispatch(stopOQSchedule({ patientId }));
    };

    const handleCopy = async (e: React.SyntheticEvent<HTMLButtonElement>) => {
        e.preventDefault();
        try {
            await navigator.clipboard.writeText(administrationURL);
            setTooltipMessage('Copied!');
            setTimeout(() => setTooltipMessage('Copy to Clipboard'), 1000);
        } catch (err) {
            setTooltipMessage('Copy with Ctrl-C');
            setTimeout(() => setTooltipMessage('Copy to Clipboard'), 1000);
        }
    };


    // The app administers OQ-45.2 natively and nothing else (mirrors the
    // gateway's 400 for any other instrument).
    const pushMeasureSupported = selectedMeasure === IN_APP_INSTRUMENT_ID;
    const canSendPush = hasDevice && pushMeasureSupported && !isLoadingDeviceStatus;
    // The last branch is the patient's own choice (MTC-170). Muted is not
    // unreachable: the request still waits in the app, so this hint explains
    // and never blocks (canSendPush ignores it). Only an explicit `false`
    // counts, because a gateway older than MTC-170 omits the field.
    const pushHint = !pushMeasureSupported
        ? 'Only OQ-45.2 can be sent to the app'
        : isLoadingDeviceStatus
            ? "Checking the patient's app…"
            : deviceStatusError
                ? "Couldn't check the patient's app"
                : !hasDevice
                    ? 'Patient has not enabled notifications in the app'
                    : deviceStatus?.notificationsEnabled === false
                        ? 'Patient has turned off notifications. They will see this the next time they open the app.'
                        : null;
    // Explains the greyed "iPhone app" option in the schedule's channel dropdown.
    // Only once the status read has answered: while it is in flight or has failed
    // we don't know whether the patient enabled notifications, and saying they
    // did not would blame them for our own gap (same precedence as pushHint).
    const schedulePushHint = deviceStatus && !hasDevice
        ? 'Patient has not enabled notifications in the app'
        : null;

    const sendChannels: Record<SendChannel, SendChannelDescriptor> = {
        sms: {
            optionLabel: hasPhone ? `Text message · ${phoneForDisplay}` : 'Text message · no phone on file',
            buttonLabel: 'Send text',
            inFlight: isSendingSMS,
            disabled: !hasPhone,
            hint: hasPhone ? null : 'No phone on file',
            onConfirm: handleSendSMS,
            confirmation: <>Are you sure you want to send the <b>{selectedMeasureLabel}</b> link to the client at <b>{phoneForDisplay}</b>?</>,
        },
        email: {
            optionLabel: clientEmail ? `Email · ${clientEmail}` : 'Email · no email on file',
            buttonLabel: 'Send email',
            inFlight: isSendingEmail,
            disabled: !clientEmail,
            hint: clientEmail ? null : 'No email on file',
            onConfirm: handleSendEmail,
            confirmation: <>Are you sure you want to send the <b>{selectedMeasureLabel}</b> link to the client at <b>{clientEmail}</b>?</>,
        },
        push: {
            optionLabel: 'iPhone app',
            buttonLabel: 'Send to iPhone app',
            inFlight: isSendingPush,
            disabled: !canSendPush,
            hint: pushHint,
            onConfirm: handleSendPush,
            confirmation: <>Send the <b>{selectedMeasureLabel}</b> questionnaire to <b>{client?.client?.name}</b>&apos;s iPhone app?</>,
        },
    };
    const selectedSend = sendChannels[sendChannel];

    const AdministrationURL = (): JSX.Element => (
        <form className="w-100">
            <div className="input-group">
                <input
                    type="text"
                    className="form-control "
                    value={administrationURL}
                    placeholder={isFetchingURL ? 'Generating link…' : 'Your link'}
                    readOnly
                />
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip">{tooltipMessage}</Tooltip>}
                >
                    <span className="input-group-btn ">
                        <button
                            className="btn btn-outline-secondary"
                            onClick={handleCopy}
                            disabled={isFetchingURL || !administrationURL}
                        >
                            Copy
                        </button>
                    </span>
                </OverlayTrigger>
            </div>
        </form>
    );

    const ScheduleSection = (): JSX.Element => (
        <div className="d-flex flex-column w-100">
            <hr className="w-100 my-2" />
            <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="mb-0 font-weight-bold">Automated sending</label>
                {schedule ? (
                    <span className="badge badge-success">
                        Active — {channelLabels[schedule.channel]}, {cadenceLabels[schedule.cadence]}
                    </span>
                ) : (
                    <span className="text-muted font-12">No automated schedule</span>
                )}
            </div>
            {schedule?.nextSendAt && (
                <small className="text-muted mb-2">Next send: {formatSendAt(schedule.nextSendAt)}</small>
            )}
            <div className="d-flex align-items-center flex-wrap mb-2">
                <label className="mr-2 mb-0" htmlFor="oq-schedule-channel">Channel:</label>
                <select
                    id="oq-schedule-channel"
                    aria-label="Schedule channel"
                    className="mr-3"
                    value={scheduleChannel}
                    onChange={(e) => setScheduleChannel(e.target.value as OQScheduleChannel)}
                    disabled={isSavingSchedule}
                >
                    {channelOptions.map(([value, label]) => (
                        <option key={value} value={value} disabled={value === 'push' && !hasDevice}>
                            {label}
                        </option>
                    ))}
                </select>
                {schedulePushHint && (
                    <small id="oq-schedule-push-hint" className="text-muted font-12 mr-3">
                        {schedulePushHint}
                    </small>
                )}
                <label className="mr-2 mb-0">Cadence:</label>
                <select
                    aria-label="Schedule cadence"
                    value={scheduleCadence}
                    onChange={(e) => setScheduleCadence(e.target.value as OQScheduleCadence)}
                    disabled={isSavingSchedule}
                >
                    {cadenceOptions.map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                    ))}
                </select>
            </div>
            <div className="d-flex align-items-center flex-wrap mb-2">
                <label className="mr-2 mb-0" htmlFor="oq-schedule-start-date">
                    {schedule ? 'Next send:' : 'First send:'}
                </label>
                <input
                    id="oq-schedule-start-date"
                    type="date"
                    className="mr-2"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    disabled={isSavingSchedule}
                />
                <select
                    aria-label="Send hour"
                    value={startHour}
                    onChange={(e) => setStartHour(Number(e.target.value))}
                    disabled={isSavingSchedule}
                >
                    {HOUR_OPTIONS.map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                    ))}
                </select>
                {!startAt && (
                    <small className="text-danger ml-2">Pick a date.</small>
                )}
                {startAtInPast && (
                    <small className="text-danger ml-2">Pick a time in the future.</small>
                )}
            </div>
            <div className="d-flex">
                <button
                    className="position-relative btn btn-primary primary-btn-color px-3 py-2 font-10 mr-2"
                    onClick={handleSaveSchedule}
                    disabled={!canSaveSchedule}
                >
                    {isSavingSchedule &&
                        <img
                            className="position-absolute m-0 p-0"
                            style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}
                            src={loadIcon}
                            alt="Loading..."
                            height="20px"
                        />}
                    <span style={{ color: isSavingSchedule ? 'transparent' : 'white' }}>
                        {schedule ? 'Update schedule' : 'Start schedule'}
                    </span>
                </button>
                {schedule && (
                    <button
                        className="btn btn-outline-secondary px-3 py-2 font-10"
                        onClick={handleStopSchedule}
                        disabled={isSavingSchedule}
                    >
                        Stop schedule
                    </button>
                )}
            </div>
        </div>
    );

    return (
        <Modal
            isOpen={isOpen}
            className="modal-dialog-centered custom-modal"
            backdrop="static"
            toggle={toggle}
            style={{
                width: '100%',
                maxWidth: 752,
                minHeight: 500,
                margin: '0.5rem auto',
            }}
        >
            <ModalHeader toggle={toggle}>Administration for {client?.client?.name}</ModalHeader>
            <ModalBody>
                <div className="d-flex flex-column justify-content-between align-items-center mb-3">
                    <div className="d-flex w-100 justify-content-between align-items-center mb-4">
                        <div className="align-items-center justify-content-center pt-2">
                            <label className="mr-2">Select measure: </label>
                            <select
                                name="measures"
                                value={selectedMeasure}
                                onChange={(e) => setSelectedMeasure(e.target.value)}
                            >
                                {measuresList.map(([value, label]) => (
                                    <option key={value} value={value}>
                                        {label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="d-flex w-100 mb-4">
                        <AdministrationURL />
                    </div>
                    <div className="d-flex w-100 justify-content-between align-items-start mb-2">
                        <div className="d-flex align-items-center">
                            <label className="mr-2 mb-0" htmlFor="oq-send-to">Send to:</label>
                            <select
                                id="oq-send-to"
                                name="sendTo"
                                value={sendChannel}
                                onChange={(e) => setSendChannel(e.target.value as SendChannel)}
                            >
                                {SEND_CHANNELS.map((channel) => (
                                    <option key={channel} value={channel}>{sendChannels[channel].optionLabel}</option>
                                ))}
                            </select>
                        </div>
                        <ConfirmSendButton
                            key={sendChannel}
                            label={selectedSend.buttonLabel}
                            inFlight={selectedSend.inFlight}
                            disabled={selectedSend.disabled}
                            hint={selectedSend.hint}
                            onConfirm={selectedSend.onConfirm}
                            className="d-flex flex-column align-items-end"
                            confirmation={selectedSend.confirmation}
                        />
                    </div>
                    <ScheduleSection />
                </div>
            </ModalBody>
        </Modal>
    );
};

export default AdministrationModal;
